import React from 'react';
import { MapPin, Users, Award, Heart, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TexasPokerCulture() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link to="/about" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to About
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-full px-6 py-3 mb-6">
            <span className="text-2xl">🤠</span>
            <span className="text-primary font-semibold">Authentic Texas Tradition</span>
          </div>
          <h1 className="text-5xl font-heading font-bold mb-6">Texas Poker Culture</h1>
          <p className="text-xl text-muted-foreground">
            Authentic Texas Hold'em taught by real Texans who know the game's social and cultural roots.
          </p>
        </div>

        <div className="prose prose-invert max-w-none">
          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <MapPin className="w-6 h-6 text-primary" />
              Born in Texas, Taught by Texans
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Texas Hold'em isn't just a card game—it's a cultural tradition that originated right here in Texas. 
              The game's name tells you where it belongs, and at Luck or Skills, we honor that heritage.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our panel consists of authentic Texans who grew up with the game, understand its social significance, 
              and can teach you not just the rules, but the spirit of Texas Hold'em as it was meant to be played.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Award className="w-6 h-6 text-primary" />
              What Texas Poker Culture Means
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Social First, Competition Second</h3>
                <p className="text-muted-foreground">
                  In Texas, poker has always been about bringing people together. It's played at kitchen tables, 
                  backyard gatherings, and community centers. The social interaction is just as important as the game itself.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Hospitality and Respect</h3>
                <p className="text-muted-foreground">
                  Texas hospitality extends to the poker table. We treat opponents with respect, welcome newcomers 
                  warmly, and maintain friendly competition. No trash talk, no ego—just good people enjoying a great game.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Stories and Strategy</h3>
                <p className="text-muted-foreground">
                  Texas poker isn't played in silence. There's conversation, laughter, and storytelling between hands. 
                  Our live sessions capture this authentic atmosphere—you're not just learning poker, you're experiencing it.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Keeping It Fun</h3>
                <p className="text-muted-foreground">
                  While we take strategy seriously, we never forget that poker should be enjoyable. Texas poker culture 
                  emphasizes fun, friendship, and good times—win or lose.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              Our Texas Panel
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Every instructor at Luck or Skills has Texas roots and decades of experience with the game. They've 
              played in countless home games, learned from Texas poker legends, and understand how the game was 
              originally played and taught.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              When you learn from our panel, you're getting authentic Texas Hold'em knowledge—not watered-down 
              online versions or aggressive casino styles. You're learning the game as it was meant to be: social, 
              strategic, and genuinely fun.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Heart className="w-6 h-6 text-primary" />
              The Texas Difference
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">🏠</span>
                <div>
                  <p className="font-semibold">Home Game Atmosphere</p>
                  <p className="text-muted-foreground text-sm">We recreate the welcoming feel of Texas home games, not intimidating casino environments.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">🤝</span>
                <div>
                  <p className="font-semibold">Community Roots</p>
                  <p className="text-muted-foreground text-sm">Texas poker brings communities together. We maintain that tradition in our online community.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">📖</span>
                <div>
                  <p className="font-semibold">Oral Tradition</p>
                  <p className="text-muted-foreground text-sm">Like Texas poker has been passed down through generations, we teach through stories and live discussion.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">🎯</span>
                <div>
                  <p className="font-semibold">Balanced Approach</p>
                  <p className="text-muted-foreground text-sm">Texas players know when to be serious and when to lighten up. We teach both strategic excellence and social enjoyment.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/30 rounded-2xl p-8">
            <h3 className="text-xl font-heading font-bold mb-3">"Kiss a Pig" - Patrick Lynch</h3>
            <p className="text-muted-foreground">
              This Texas saying perfectly captures our philosophy: poker should be fun, a little cheeky, and never 
              take itself too seriously. That's the Texas way, and that's how we teach the game.
            </p>
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/membership" 
              className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105"
            >
              Experience Texas Poker
              <span className="text-xl">🤠</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}